import {useCallback, useLayoutEffect, useState, type RefObject} from 'react';

export type UseFillRemainingViewportOptions = {
    /** Pixels reserved below the element (page chrome / status). Default 8. */
    bottomGap?: number;
    /** When false, skip measuring. Default true. */
    enabled?: boolean;
};

/**
 * Measure remaining viewport height from the element's top edge.
 * Pair with an inner scroller (e.g. TableContainer overflow:auto) so the page shell does not scroll.
 */
export function useFillRemainingViewport(
    ref: RefObject<HTMLElement | null>,
    options?: UseFillRemainingViewportOptions,
): number | undefined {
    const bottomGap = options?.bottomGap ?? 8;
    const enabled = options?.enabled ?? true;
    const [height, setHeight] = useState<number | undefined>(undefined);

    const measure = useCallback(() => {
        if (!enabled) {
            setHeight(undefined);
            return;
        }
        const el = ref.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        const next = Math.max(120, Math.floor(window.innerHeight - top - bottomGap));
        setHeight((prev) => (prev === next ? prev : next));
    }, [ref, bottomGap, enabled]);

    useLayoutEffect(() => {
        if (!enabled) {
            setHeight(undefined);
            return;
        }
        measure();
        window.addEventListener('resize', measure);
        const vv = window.visualViewport;
        vv?.addEventListener('resize', measure);
        vv?.addEventListener('scroll', measure);

        const el = ref.current;
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
        if (el) {
            ro?.observe(el);
            if (el.parentElement) ro?.observe(el.parentElement);
        }

        return () => {
            window.removeEventListener('resize', measure);
            vv?.removeEventListener('resize', measure);
            vv?.removeEventListener('scroll', measure);
            ro?.disconnect();
        };
    }, [measure, enabled, ref]);

    return height;
}
